// Steps, timings and geometry for the Cursor Continuity diagram
// (continuity-diagram.tsx). Desktop coordinates come straight from the design's
// 1922×1082 scene; the phone layout keeps its topology (both nodes and the two
// WAL boxes stacked, every wire running down one bus on the right) in its own
// narrower scene.

/** The ref main points at before the push, and the commit the push lands. */
export const STALE = "a91e"
export const CURRENT = "3f2a"

export type WireId =
  | "pack"
  | "uploadWal"
  | "cas"
  | "gossip"
  | "catchUp"
  | "fetch"
  | "check"
  | "ret"
  | "serve"
  | "ack"
export type PillId =
  | "pack"
  | "ack"
  | "uploadWal"
  | "cas"
  | "catchUp"
  | "fetch"
  | "check"
  | "indexCurrent"
  | "packTip"
export type ChipId = "fetchTab" | "nodeA" | "nodeB" | "walObj" | "walIndex"
export type StatusId = "nodeA" | "nodeB" | "walObj" | "walIndex"

export const WIRE_IDS: WireId[] = [
  "pack",
  "uploadWal",
  "cas",
  "gossip",
  "catchUp",
  "fetch",
  "check",
  "ret",
  "serve",
  "ack",
]
export const CHIP_IDS: ChipId[] = ["fetchTab", "nodeA", "nodeB", "walObj", "walIndex"]
export const STATUS_IDS: StatusId[] = ["nodeA", "nodeB", "walObj", "walIndex"]

/** Pill labels; an array is a label set on two lines. */
export const PILL_LABELS: Record<PillId, string | string[]> = {
  pack: "pack",
  ack: "ack ✓",
  uploadWal: "upload WAL",
  cas: "publish CAS",
  catchUp: "catch up",
  fetch: "fetch",
  check: "check eTag",
  indexCurrent: ["index", "current"],
  packTip: "pack + tip",
}

/** Hover explanations: a title and a sentence for each part of the scene. */
export interface Tip {
  title: string
  body: string
}

export type BoxId = "push" | "fetch" | "nodeA" | "nodeB" | "s3" | "walObj" | "walIndex"

export const BOX_TIPS: Record<BoxId, Tip> = {
  push: {
    title: "Push client",
    body: "The push is acknowledged only after its WAL object is durable and its pointer is published.",
  },
  fetch: {
    title: "Fetch client",
    body: "This fetch is independent of the push and can enter through a different warm cache.",
  },
  nodeA: {
    title: "Node A · push lane",
    body: "Node A owns this push session. It writes the pack locally, prepares the ref, uploads the WAL object, and publishes its pointer with a WAL-index CAS.",
  },
  nodeB: {
    title: "Node B · fetch lane",
    body: "Node B checks the WAL index before serving. If its cache is stale, it catches up from the WAL first.",
  },
  s3: {
    title: "S3 · source of truth",
    body: "S3 holds the durable WAL object and WAL index. Node A publishes a durable WAL entry by atomically updating the index pointer.",
  },
  walObj: {
    title: "WAL object",
    body: "An immutable, content-addressed WAL entry — the pack plus the ref update, durable the moment the upload completes.",
  },
  walIndex: {
    title: "WAL index",
    body: "A single pointer to the latest WAL entry. Node A moves it with an atomic CAS; that CAS is what makes the push visible.",
  },
}

/** Wire tips sit on the wire's pill, so they're only hoverable while it shows. */
export const PILL_TIPS: Record<PillId, string> = {
  pack: "Node A receives the pack directly from the push client over this connection.",
  ack: "The push is acknowledged only after its WAL object is durable and its pointer is published.",
  uploadWal: "Node A uploads the pack as a new WAL object to S3.",
  cas: "Node A publishes the pointer with an atomic compare-and-swap on the WAL index.",
  catchUp:
    "Node B catches up by reading the WAL object directly, when gossip didn't arrive or its cache was already stale.",
  fetch: "A fetch client's request enters through Node B.",
  check: "Node B checks the WAL index to see whether its local cache is still current.",
  indexCurrent: "The WAL index confirms Node B's cache is current, so it can serve the fetch.",
  packTip: "Node B serves the pack and ref tip back to the fetch client.",
}

/** Gossip has no pill, so its tip sits on a wide invisible stroke along the wire. */
export const GOSSIP_TIP: Tip = {
  title: "gossip",
  body: "Node A gossips the new tip to Node B. If this is missed, Node B just finds out on its next fetch instead.",
}

export interface Phase {
  wires: WireId[]
  pills: PillId[]
  chips: Record<ChipId, string>
  status: Record<StatusId, string | null>
  caption: string
  /** How long the step holds, in ms. */
  duration: number
}

export const PHASES: Phase[] = [
  {
    wires: ["pack", "uploadWal"],
    pills: ["pack"],
    chips: { fetchTab: STALE, nodeA: STALE, nodeB: STALE, walObj: STALE, walIndex: STALE },
    status: {
      nodeA: `receiving pack ${CURRENT}`,
      nodeB: null,
      walObj: `uploading ${CURRENT}`,
      walIndex: null,
    },
    caption: "Node A takes the pack and streams the WAL object toward S3.",
    duration: 4400,
  },
  {
    wires: [],
    pills: [],
    chips: { fetchTab: STALE, nodeA: STALE, nodeB: STALE, walObj: CURRENT, walIndex: STALE },
    status: { nodeA: `prepared main ${CURRENT}`, nodeB: null, walObj: "durable", walIndex: null },
    caption: "The WAL object is durable in S3.",
    duration: 4400,
  },
  {
    wires: ["cas"],
    pills: ["cas"],
    chips: { fetchTab: STALE, nodeA: CURRENT, nodeB: STALE, walObj: CURRENT, walIndex: CURRENT },
    status: { nodeA: null, nodeB: null, walObj: null, walIndex: "published" },
    caption: `Node A prepares main → ${CURRENT} and publishes the pointer with a WAL-index CAS.`,
    // There's more to take in on this step, so it holds longer.
    duration: 6400,
  },
  {
    wires: ["ack", "gossip", "catchUp"],
    pills: ["ack", "catchUp"],
    chips: {
      fetchTab: STALE,
      nodeA: CURRENT,
      nodeB: CURRENT,
      walObj: CURRENT,
      walIndex: CURRENT,
    },
    status: { nodeA: null, nodeB: "cache updated", walObj: null, walIndex: null },
    caption: "The push is acknowledged; Node A gossips changes to Node B.",
    duration: 4400,
  },
  {
    wires: ["gossip", "fetch", "check"],
    pills: ["fetch", "check"],
    chips: {
      fetchTab: STALE,
      nodeA: CURRENT,
      nodeB: CURRENT,
      walObj: CURRENT,
      walIndex: CURRENT,
    },
    status: { nodeA: null, nodeB: null, walObj: null, walIndex: null },
    caption: "A separate fetch checks the WAL index at Node B.",
    duration: 4400,
  },
  {
    wires: ["gossip", "ret", "serve"],
    pills: ["indexCurrent", "packTip"],
    chips: {
      fetchTab: CURRENT,
      nodeA: CURRENT,
      nodeB: CURRENT,
      walObj: CURRENT,
      walIndex: CURRENT,
    },
    status: { nodeA: null, nodeB: null, walObj: null, walIndex: null },
    caption: "Node B's cache is current and serves the fetch.",
    duration: 5200,
  },
]

/**
 * A chip keeps its old value for this long into a step, keyed by step index:
 * Node B's chip waits for the catch-up wire to arrive, and the fetch client's
 * chip waits for both replies on the last step.
 */
export const CHIP_HOLD: Partial<Record<ChipId, Record<number, number>>> = {
  nodeB: { 3: 950 },
  fetchTab: { 5: 2000 },
}

/** A pill shows this long into a step, keyed by step index: the last step builds in one reply at a time. */
export const PILL_DELAY: Partial<Record<PillId, Record<number, number>>> = {
  indexCurrent: { 5: 950 },
  packTip: { 5: 1650 },
}

/** Statuses that are still working: they re-flip to themselves and breathe while they hold. */
export const RESTLESS = new Set([`receiving pack ${CURRENT}`, `uploading ${CURRENT}`])

interface Rect {
  x: number
  y: number
  w: number
}

export interface Layout {
  viewBox: [number, number, number, number]
  push: Rect
  fetch: Rect
  nodeA: Rect
  nodeB: Rect
  walObj: Rect
  walIndex: Rect
  s3: Rect & { h: number }
  wires: Record<WireId, [number, number][]>
  pills: Record<PillId, [number, number]>
}

export const DESKTOP_LAYOUT: Layout = {
  viewBox: [0, 0, 1922, 1082],
  push: { x: 735, y: 95, w: 178 },
  fetch: { x: 1009, y: 95, w: 263 },
  nodeA: { x: 373, y: 299, w: 540 },
  nodeB: { x: 1006, y: 299, w: 540 },
  walObj: { x: 402, y: 763, w: 540 },
  walIndex: { x: 976, y: 763, w: 540 },
  s3: { x: 374, y: 672, w: 1171, h: 310 },
  wires: {
    pack: [
      [826, 161],
      [826, 219],
      [640, 219],
      [640, 297],
    ],
    // The pack's route back, carrying the ack to the push client.
    ack: [
      [640, 297],
      [640, 219],
      [826, 219],
      [826, 161],
    ],
    uploadWal: [
      [643, 492],
      [643, 606],
      [826, 606],
      [826, 761],
    ],
    cas: [
      [643, 492],
      [643, 585],
      [1254, 585],
      [1254, 761],
    ],
    gossip: [
      [915, 395],
      [1004, 395],
    ],
    // Node B pulls from the WAL object, so the wire ends at Node B.
    catchUp: [
      [765, 761],
      [765, 585],
      [1276, 585],
      [1276, 492],
    ],
    // The client's request; serve is the same route back with the reply.
    fetch: [
      [1141, 161],
      [1141, 239],
      [1278, 239],
      [1278, 297],
    ],
    check: [
      [1278, 492],
      [1278, 578],
      [1245, 578],
      [1245, 761],
    ],
    ret: [
      [1245, 761],
      [1245, 584],
      [1286, 584],
      [1286, 492],
    ],
    serve: [
      [1278, 297],
      [1278, 239],
      [1141, 239],
      [1141, 161],
    ],
  },
  pills: {
    pack: [730, 219],
    ack: [730, 219],
    uploadWal: [734, 606],
    cas: [948, 585],
    catchUp: [1050, 585],
    fetch: [1207, 239],
    packTip: [1210, 239],
    check: [1245, 668],
    indexCurrent: [1245, 668],
  },
}

export const MOBILE_LAYOUT: Layout = {
  viewBox: [0, -40, 704, 1240],
  push: { x: 60, y: 20, w: 178 },
  fetch: { x: 290, y: 20, w: 263 },
  nodeA: { x: 44, y: 170, w: 480 },
  nodeB: { x: 44, y: 401, w: 480 },
  walObj: { x: 44, y: 710, w: 480 },
  walIndex: { x: 44, y: 925, w: 480 },
  s3: { x: 20, y: 632, w: 528, h: 508 },
  wires: {
    pack: [
      [151, 86],
      [151, 168],
    ],
    ack: [
      [151, 168],
      [151, 86],
    ],
    uploadWal: [
      [526, 250],
      [610, 250],
      [610, 790],
      [527, 790],
    ],
    cas: [
      [526, 250],
      [610, 250],
      [610, 1005],
      [527, 1005],
    ],
    gossip: [
      [526, 250],
      [610, 250],
      [610, 481],
      [527, 481],
    ],
    catchUp: [
      [526, 790],
      [610, 790],
      [610, 481],
      [527, 481],
    ],
    fetch: [
      [421, 86],
      [421, 126],
      [650, 126],
      [650, 481],
      [527, 481],
    ],
    check: [
      [526, 481],
      [610, 481],
      [610, 1005],
      [527, 1005],
    ],
    ret: [
      [526, 1005],
      [610, 1005],
      [610, 481],
      [527, 481],
    ],
    serve: [
      [526, 481],
      [650, 481],
      [650, 126],
      [421, 126],
      [421, 86],
    ],
  },
  pills: {
    pack: [151, 127],
    ack: [151, 127],
    uploadWal: [610, 612],
    cas: [610, 612],
    catchUp: [610, 636],
    fetch: [535, 126],
    packTip: [535, 126],
    check: [610, 720],
    indexCurrent: [610, 720],
  },
}
